import {
    collection,
    query,
    getDocs,
    where,
    getFirestore,
    setDoc,
    doc,
    QueryDocumentSnapshot,
    DocumentData,
    WithFieldValue,
    getDoc,
    writeBatch,
    updateDoc,
} from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import UserRoles from '../../../resources/enums/UserRoles';
import DbCollections from '../../../resources/enums/DbCollections';
import { User, Notification, UserMission, UserStage } from '../types';
import { resetPasswordFirebase } from '../../auth/repository';
import _, { some } from 'lodash';
import { db } from '../../../config/firebase';
import { convertTimestampToDate } from '../../../utils/Converter';
import NotificationStatuses from '../../../resources/enums/NotificationStatuses';
import SurveyStatuses from '../../../resources/enums/SurveyStatuses';

const createUser = async (email: string) => {
    const auth = getAuth();
    const password = Math.random().toString(36).slice(-8);
    return createUserWithEmailAndPassword(auth, email, password)
        .then((userCredential) => {
            resetPasswordFirebase(email);
            return userCredential.user.uid;
        })
        .catch(() => {
            return undefined;
        });
};

const BATCH_SIZE_IN = 10;

function splitByBatchSize<T>(objs: T[], batchSize: number) {
    const ret = [] as T[][];
    for (let i = 0; i < objs.length; i += batchSize) {
        ret.push(objs.slice(i, i + batchSize));
    }
    return ret;
}

const mapSnapshotToUser = (doc: QueryDocumentSnapshot<DocumentData>, roles: string[], masters?: User[]) => {
    return {
        uid: doc.id,
        firstName: doc.data().firstName,
        lastName: doc.data().lastName,
        email: doc.data().email,
        registrationDate: convertTimestampToDate(doc.data().registrationDate),
        userName: doc.data().userName,
        role: _.filter(doc.data().role, (o) => {
            roles.includes(o);
            return o;
        }).toString(),
        missions: doc.data().missions,
        master: doc.data().master ? masters?.find((x) => x.uid === doc.data().master) : undefined,
        contactTypes: doc.data().contactTypes,
    } as User;
};

const mapUserToSnapshot = (user: User, registrationDate: number) => {
    const roles = user.role.split(',');
    return {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        isOnline: false,
        registrationDate: new Date(registrationDate),
        friends: [],
        invitations: [],
        notifications: [],
        picture: '',
        userName: '',
        language: 'en',
        role: roles,
        master: user.master?.uid ?? '',
        contactTypes: user.contactTypes,
    } as WithFieldValue<DocumentData>;
};

const getMissions = async (userId: string) => {
    const querySnapshot = await getDocs(query(collection(db, DbCollections.Users, userId, DbCollections.Missions)));
    let missions = [] as UserMission[];
    querySnapshot.forEach((doc) => {
        missions.push(doc.data() as UserMission);
    });
    missions = await Promise.all(
        missions.map(
            async (mission) =>
                ({
                    ...mission,
                    stages: await getStages(userId, mission.id),
                } as UserMission),
        ),
    );
    return missions;
};

const getStages = async (userId: string, missionId: string) => {
    const querySnapshot = await getDocs(
        query(collection(db, DbCollections.Users, userId, DbCollections.Missions, missionId, DbCollections.Stages)),
    );
    const stages = [] as UserStage[];
    querySnapshot.forEach((doc) => {
        stages.push({
            doneCountExercises: doc.data().doneCountExercises,
            id: doc.data().id,
            isSeen: doc.data().isSeen,
            surveyStatus: doc.data().surveyStatus === SurveyStatuses.done,
        });
    });
    return stages.map((stage) => {
        return { ...stage, surveyStatus: stages.some((x) => x.surveyStatus) };
    });
};

const adminRoles = [UserRoles.Admin, UserRoles.SuperAdmin, UserRoles.Coach] as string[];
const userRoles = [UserRoles.User, UserRoles.Master] as string[];

const getMasters = async () => {
    const q = query(collection(getFirestore(), DbCollections.Users), where('role', 'array-contains', UserRoles.Master));
    const querySnapshot = await getDocs(q);
    const masters = [] as User[];
    querySnapshot.forEach((doc) => {
        masters.push(mapSnapshotToUser(doc, [UserRoles.Master]));
    });
    return masters;
};

export const getUsersFirebase = async (roles: string[]) => {
    const masters = await getMasters();
    const q = query(collection(getFirestore(), DbCollections.Users), where('role', 'array-contains-any', roles));
    const querySnapshot = await getDocs(q);
    const users = [] as User[];
    querySnapshot.forEach((doc) => {
        users.push(mapSnapshotToUser(doc, roles, masters));
    });

    const usersWithMissions = [] as User[];
    const batchSize = 100;

    for (let i = 0; i < users.length; i += batchSize) {
        const batchUsers = users.slice(i, i + batchSize);
        const batchUserMissions = await Promise.all(
            batchUsers.map(
                async (user) =>
                    ({
                        ...user,
                        missions: await getMissions(user.uid),
                    } as User),
            ),
        );
        usersWithMissions.push(...batchUserMissions);
    }
    return usersWithMissions;
};

export const getUsersFirebaseByIds = async (uids: string[], roles: string[]) => {
    const processBatch = async (batchUids: string[]) => {
        const q = query(collection(db, DbCollections.Users), where('__name__', 'in', batchUids));
        const snapshot = await getDocs(q);
        return snapshot.docs
            .filter((d) => d.data().role.some((r: string) => roles.includes(r)))
            .map((x) => mapSnapshotToUser(x, roles));
    };
    const promises = splitByBatchSize(uids, BATCH_SIZE_IN).map(processBatch);
    return ([] as User[]).concat(...(await Promise.all(promises)));
};

export const addUserFirebase = async (user: User) => {
    const registrationDate = new Date(Date.now()).getTime();
    const q = query(collection(db, DbCollections.Users), where('email', '==', user.email));
    const users = await getDocs(q);
    if (users.size > 0) {
        const existingUser = users.docs[0];
        const uid = existingUser.id;
        const currentRoles = existingUser.data().role;
        if (currentRoles.includes(user.role)) throw new Error('User with this role already exists.');
        const updated = {
            ...existingUser.data(),
            role: some(currentRoles, (item) => adminRoles.includes(item))
                ? currentRoles.map((item: string) => (adminRoles.includes(item) ? user.role : item))
                : [...existingUser.data().role, user.role],
        };
        await setDoc(doc(db, DbCollections.Users, uid), updated);
        return { ...user, uid: uid, registrationDate: convertTimestampToDate(existingUser.data().registrationDate) };
    } else {
        return createUser(user.email)
            .then(async (uid) => {
                if (!uid) throw new Error('User not exist!');
                await setDoc(doc(db, DbCollections.Users, uid), mapUserToSnapshot(user, registrationDate));
                user.uid = uid;
                user.registrationDate = new Date(registrationDate).toLocaleDateString('en-US');
                return user;
            })
            .catch((err) => {
                throw new Error(err);
            });
    }
};

export const deleteUserFirebase = async (uid: string) => {
    const docRef = doc(db, DbCollections.Users, uid);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return undefined;
    const roles = docSnap.data().role;

    await updateDoc(doc(db, DbCollections.Users, uid), {
        role: _.filter(roles, (item) => {
            if (
                item !== UserRoles.Admin &&
                item != UserRoles.SuperAdmin &&
                item != UserRoles.Coach &&
                item != UserRoles.User
            )
                return item;
        }),
    });
    return uid;
};

export const editUserFirebase = async (uid: string, newRole: string) => {
    const user = await getDoc(doc(db, DbCollections.Users, uid));
    if (!user.exists()) return undefined;
    await updateDoc(doc(db, DbCollections.Users, uid), { role: newRole.split(',') });
    return newRole;
};

export const sendGlobalNotificationFirebase = async (type: string) => {
    const q = query(collection(getFirestore(), DbCollections.Users), where('role', 'array-contains-any', userRoles));
    const querySnapshot = await getDocs(q);
    const notification = {
        type: type,
        status: NotificationStatuses.Latest,
    } as Notification;
    const batchSize = 499;
    const totalUsers = querySnapshot.size;
    const totalBatches = Math.ceil(totalUsers / batchSize);

    for (let i = 0; i < totalBatches; i++) {
        const batch = writeBatch(db);
        const startIndex = i * batchSize;
        const endIndex = startIndex + batchSize;

        const usersInBatch = querySnapshot.docs.slice(startIndex, endIndex);

        usersInBatch.forEach((user) => {
            batch.set(user.ref, { ...user.data(), notifications: [...user.data().notifications, notification] });
        });
        await batch.commit();
    }
};

export const sendUserNotificationFirebase = async (type: string, uid: string, uidFrom: string) => {
    const userRef = doc(db, DbCollections.Users, uid);
    const user = await getDoc(userRef);
    if (!user.exists()) return undefined;
    const notification = {
        type,
        status: NotificationStatuses.Latest,
        userId: uidFrom,
    } as Notification;
    const userData = user.data();
    const notifications = userData.notifications || [];
    notifications.push(notification);

    await updateDoc(userRef, {
        notifications: notifications,
    });
};
